import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { divideAtScale, normalizeWeiString } from '../data/data-transform';
import { CUSTOS_ENVIRONMENT } from '../models/environment';
import {
  ApiMarket,
  ApiMarketPrice,
  ApiCycle,
  ApiOrder,
  ApiOrderSettlement,
  ApiLocker,
  ApiLockerOrder,
  ApiLockerUnlockOrder,
  BetSignatureRequest,
  BetSignatureResponse,
  TransferMessageHashRequest,
  TransferMessageHashResponse,
  ApiTvl,
  ApiTvlHistory,
  ApiPriceHistory,
  ApiTokenInfo,
  StakingRewardPending,
  StakingRewardSnapshot,
  LockerRecord,
  LockerClaim,
  PaginatedResponse,
  PagedResult,
  MarketQueryParams,
  CycleQueryParams,
  LockerQueryParams,
  LockerListQueryParams,
  LockerOrderQueryParams,
  OrderQueryParams,
  PaginatedQueryParams,
  TableSchemaColumn,
  TableRowsResponse,
  TableRowsParams,
} from './api.types';
import type { CustosApi } from './api.provider';
import {
  MOCK_TVL_DATA,
  MOCK_TVL_HISTORY,
  MOCK_PRICE_HISTORY,
  MOCK_TOKEN_INFO,
} from './api-mock.data';

/** Tables the FE legitimately reads via the generic /table/{name} endpoint
 *  in production. Everything else throws the debug-disabled error to keep
 *  the schema-introspection surface locked down. Add new tables here only
 *  with explicit FE/BE sign-off. */
const PROD_ALLOWED_TABLES = new Set<string>(['custos_event']);

@Injectable({ providedIn: 'root' })
export class ApiService implements CustosApi {
  private readonly http = inject(HttpClient);
  private readonly env = inject(CUSTOS_ENVIRONMENT);

  private get baseUrl(): string {
    return this.env.apiBaseUrl;
  }

  private get headers(): HttpHeaders {
    const h = new HttpHeaders();
    // API key should be injected by proxy/BFF, not from client-side bundle
    // Only add if explicitly set (dev convenience, never in production)
    return this.env.apiKey ? h.set('X-API-Key', this.env.apiKey) : h;
  }

  /**
   * Unwrap V2 paginated envelope { data: T[] } or pass through V1 bare array T[].
   * Compatible with both backend versions during migration.
   */
  private unwrapList<T>(): (source: Observable<T[] | PaginatedResponse<T>>) => Observable<T[]> {
    return (source) =>
      source.pipe(
        map((response) => {
          if (Array.isArray(response)) return response;
          if (response != null && Array.isArray(response.data)) return response.data;
          return [];
        }),
      );
  }

  /**
   * Unwrap V2 paginated envelope into PagedResult with hasMore detection.
   * Falls back safely for V1 bare arrays (hasMore = false).
   */
  private unwrapPage<T>(): (source: Observable<T[] | PaginatedResponse<T>>) => Observable<PagedResult<T>> {
    return (source) =>
      source.pipe(
        map((response) => {
          if (Array.isArray(response)) {
            return { items: response, total: response.length, hasMore: false };
          }
          if (response != null && Array.isArray(response.data)) {
            const { data, total, offset, limit } = response;
            return { items: data, total, hasMore: offset + data.length < total };
          }
          return { items: [], total: 0, hasMore: false };
        }),
      );
  }

  private appendPagination(
    httpParams: HttpParams,
    params: PaginatedQueryParams,
  ): HttpParams {
    if (params.limit !== undefined)
      httpParams = httpParams.set('limit', params.limit);
    if (params.offset !== undefined)
      httpParams = httpParams.set('offset', params.offset);
    return httpParams;
  }

  // ------------------------------------------------------------------
  // Wei → human normalisers
  //
  // The previous dev gateway (CF Worker) pre-divided raw wei strings into
  // decimal form (`toHuman`). The real FastAPI upstream does NOT — it ships
  // raw wei. Every consumer in the app expects the already-divided shape,
  // so we re-apply that normalisation here at the service boundary. This is
  // strictly a compat shim: once the upstream is treated as canonical we
  // can delete the callsites that still require it.
  //
  // The deterministic divisor is `divideAtScale` (imported from data-transform).
  // It ALWAYS divides by 10^scale — no magnitude heuristic — and handles both
  // integer raw ("2000000000000") and fractional pre-normalized
  // ("52886026259240.002167") shapes. The right scale is picked at each call
  // site (internalScale=18-d for pool/order amounts; 18 for prizes/prices).
  // ------------------------------------------------------------------

  private normalizeCycle(c: ApiCycle): ApiCycle {
    const d = c.base_token_decimals ?? 18;
    // Empirical contract (verified 2026-04-29 against staging API):
    //   The smart contract stores all token amounts at 18-dec INTERNAL
    //   precision regardless of base_token_decimals. The backend ships that
    //   internal value undivided. For 18-dec base tokens it coincidentally
    //   looks human ("700" = 700 DEVAI). For 6-dec USDT it's over-scaled by
    //   10^(18 - decimals) = 10^12 — a 2-USDT bet ships as "2000000000000".
    // Wesley reported 2026-04-28: 2 USDT bet displayed as 2,000,000.
    // Right divisor is 10^(18 - d), not 10^d.
    const internalScale = 18 - d;
    // Two scaling regimes verified against staging API 2026-04-29:
    //   1. POOL totals + per-order amount/shares use *contract internal* scale,
    //      = `18 - base_token_decimals` zeros above human. For 18-dec tokens
    //      internalScale=0 (already-human); for 6-dec USDT it's 12.
    //   2. Price feeds (Chainlink/Pancake/DexScreener) AND prize_amount use
    //      *fixed 18-dec* scale regardless of token. Verified:
    //         DEVAI 18-dec prize "970000000000000000000000" /10^18 = 970,000
    //         USDT  6-dec  prize "1940000000000000000"      /10^18 = 1.94
    return {
      ...c,
      start_price: divideAtScale(c.start_price, 18),
      final_price: divideAtScale(c.final_price, 18),
      prize_amount: divideAtScale(c.prize_amount, 18),
      side_up_total: divideAtScale(c.side_up_total ?? c.side_up_total_raw, internalScale) ?? '0',
      side_down_total: divideAtScale(c.side_down_total ?? c.side_down_total_raw, internalScale) ?? '0',
      shares_up_total: divideAtScale(c.shares_up_total ?? c.shares_up_total_raw, internalScale) ?? '0',
      shares_down_total: divideAtScale(c.shares_down_total ?? c.shares_down_total_raw, internalScale) ?? '0',
    };
  }

  private normalizeOrder(o: ApiOrder): ApiOrder {
    const d = o.base_token_decimals ?? 18;
    const internalScale = 18 - d;
    return {
      ...o,
      amount: divideAtScale(o.amount ?? o.amount_raw, internalScale) ?? '0',
      share: divideAtScale(o.share ?? o.share_raw, internalScale) ?? '0',
      reward: o.reward != null ? divideAtScale(o.reward, internalScale) ?? undefined : o.reward,
    };
  }

  private normalizeSettlement(s: ApiOrderSettlement): ApiOrderSettlement {
    const d = s.base_token_decimals ?? 18;
    const internalScale = 18 - d;
    return { ...s, reward: divideAtScale(s.reward ?? s.reward_raw, internalScale) ?? '0' };
  }

  private normalizeLockerOrder(o: ApiLockerOrder): ApiLockerOrder {
    return { ...o, amount: normalizeWeiString(o.amount, 18) ?? '0' };
  }

  // =========================================================================
  // Health (public — no auth required)
  // =========================================================================

  health(): Observable<{ status: string }> {
    return this.http.get<{ status: string }>(`${this.baseUrl}/health`);
  }

  // =========================================================================
  // Markets
  // =========================================================================

  getMarkets(): Observable<ApiMarket[]> {
    return this.http.get<ApiMarket[]>(`${this.baseUrl}/markets`, {
      headers: this.headers,
    });
  }

  getMarket(params: MarketQueryParams): Observable<ApiMarket> {
    const httpParams = new HttpParams()
      .set('chain_id', params.chain_id)
      .set('market_id', params.market_id);
    return this.http.get<ApiMarket>(`${this.baseUrl}/market`, {
      headers: this.headers,
      params: httpParams,
    });
  }

  getMarketPrice(params: MarketQueryParams): Observable<ApiMarketPrice> {
    const httpParams = new HttpParams()
      .set('chain_id', params.chain_id);
    return this.http.get<ApiMarketPrice>(
      `${this.baseUrl}/market/${params.market_id}/price`,
      { headers: this.headers, params: httpParams },
    );
  }

  // =========================================================================
  // Cycles
  // =========================================================================

  getCycles(
    params: MarketQueryParams & PaginatedQueryParams,
  ): Observable<ApiCycle[]> {
    let httpParams = new HttpParams()
      .set('chain_id', params.chain_id)
      .set('market_id', params.market_id);
    httpParams = this.appendPagination(httpParams, params);
    return this.http
      .get<PaginatedResponse<ApiCycle>>(`${this.baseUrl}/cycles`, {
        headers: this.headers,
        params: httpParams,
      })
      .pipe(
        this.unwrapList(),
        map((list) => list.map((c) => this.normalizeCycle(c))),
      );
  }

  getCycle(params: CycleQueryParams): Observable<ApiCycle> {
    const httpParams = new HttpParams()
      .set('chain_id', params.chain_id)
      .set('market_id', params.market_id)
      .set('cycle_id', params.cycle_id);
    return this.http
      .get<ApiCycle>(`${this.baseUrl}/cycle`, { headers: this.headers, params: httpParams })
      .pipe(map((c) => this.normalizeCycle(c)));
  }

  // =========================================================================
  // Orders
  // =========================================================================

  getOrders(params: OrderQueryParams): Observable<ApiOrder[]> {
    let httpParams = new HttpParams()
      .set('chain_id', params.chain_id)
      .set('market_id', params.market_id);
    if (params.user_address)
      httpParams = httpParams.set('user_address', params.user_address);
    httpParams = this.appendPagination(httpParams, params);
    return this.http
      .get<PaginatedResponse<ApiOrder>>(`${this.baseUrl}/orders`, {
        headers: this.headers,
        params: httpParams,
      })
      .pipe(
        this.unwrapList(),
        map((list) => list.map((o) => this.normalizeOrder(o))),
      );
  }

  getOrdersByCycle(
    params: CycleQueryParams & PaginatedQueryParams & { user_address?: string },
  ): Observable<ApiOrder[]> {
    let httpParams = new HttpParams()
      .set('chain_id', params.chain_id)
      .set('market_id', params.market_id)
      .set('cycle_id', params.cycle_id);
    if (params.user_address)
      httpParams = httpParams.set('user_address', params.user_address);
    httpParams = this.appendPagination(httpParams, params);
    return this.http
      .get<PaginatedResponse<ApiOrder>>(`${this.baseUrl}/orders_by_cycle`, {
        headers: this.headers,
        params: httpParams,
      })
      .pipe(
        this.unwrapList(),
        map((list) => list.map((o) => this.normalizeOrder(o))),
      );
  }

  // =========================================================================
  // Order Settlements
  // =========================================================================

  getOrderSettlements(
    params: OrderQueryParams,
  ): Observable<ApiOrderSettlement[]> {
    let httpParams = new HttpParams()
      .set('chain_id', params.chain_id)
      .set('market_id', params.market_id);
    if (params.user_address)
      httpParams = httpParams.set('user_address', params.user_address);
    httpParams = this.appendPagination(httpParams, params);
    return this.http
      .get<PaginatedResponse<ApiOrderSettlement>>(
        `${this.baseUrl}/order_settlements`,
        { headers: this.headers, params: httpParams },
      )
      .pipe(
        this.unwrapList(),
        map((list) => list.map((s) => this.normalizeSettlement(s))),
      );
  }

  // =========================================================================
  // Paged variants (preserve hasMore for UI pagination)
  // =========================================================================

  getOrdersPaged(params: OrderQueryParams): Observable<PagedResult<ApiOrder>> {
    let httpParams = new HttpParams()
      .set('chain_id', params.chain_id)
      .set('market_id', params.market_id);
    if (params.user_address)
      httpParams = httpParams.set('user_address', params.user_address);
    httpParams = this.appendPagination(httpParams, params);
    return this.http
      .get<PaginatedResponse<ApiOrder>>(`${this.baseUrl}/orders`, {
        headers: this.headers,
        params: httpParams,
      })
      .pipe(
        this.unwrapPage(),
        map((page) => ({ ...page, items: page.items.map((o) => this.normalizeOrder(o)) })),
      );
  }

  getOrderSettlementsPaged(params: OrderQueryParams): Observable<PagedResult<ApiOrderSettlement>> {
    let httpParams = new HttpParams()
      .set('chain_id', params.chain_id)
      .set('market_id', params.market_id);
    if (params.user_address)
      httpParams = httpParams.set('user_address', params.user_address);
    httpParams = this.appendPagination(httpParams, params);
    return this.http
      .get<PaginatedResponse<ApiOrderSettlement>>(
        `${this.baseUrl}/order_settlements`,
        { headers: this.headers, params: httpParams },
      )
      .pipe(
        this.unwrapPage(),
        map((page) => ({ ...page, items: page.items.map((s) => this.normalizeSettlement(s)) })),
      );
  }

  getLockerOrdersPaged(params: LockerOrderQueryParams): Observable<PagedResult<ApiLockerOrder>> {
    let httpParams = new HttpParams()
      .set('chain_id', params.chain_id);
    if (params.locker_id)
      httpParams = httpParams.set('locker_id', params.locker_id);
    if (params.user_address)
      httpParams = httpParams.set('user_address', params.user_address);
    httpParams = this.appendPagination(httpParams, params);
    return this.http
      .get<PaginatedResponse<ApiLockerOrder>>(
        `${this.baseUrl}/locker_orders`,
        { headers: this.headers, params: httpParams },
      )
      .pipe(
        this.unwrapPage(),
        map((page) => ({ ...page, items: page.items.map((o) => this.normalizeLockerOrder(o)) })),
      );
  }

  // =========================================================================
  // Locker
  // =========================================================================

  getLockers(params: LockerListQueryParams): Observable<ApiLocker[]> {
    // Backend has no /lockers endpoint — use /table/locker and filter by chain_id
    return this.http
      .get<TableRowsResponse>(`${this.baseUrl}/table/locker`, {
        headers: this.headers,
        params: new HttpParams().set('limit', 100),
      })
      .pipe(
        map((res) => {
          const rows = (res.rows ?? []) as unknown as ApiLocker[];
          const filtered = rows.filter((r) => r.chain_id === params.chain_id);
          // Dedup by locker_id — D1 sync can temporarily produce duplicate rows
          return [...new Map(filtered.map((r) => [r.locker_id, r])).values()];
        }),
      );
  }

  getLocker(params: LockerQueryParams): Observable<ApiLocker> {
    const httpParams = new HttpParams()
      .set('chain_id', params.chain_id)
      .set('locker_id', params.locker_id);
    return this.http.get<ApiLocker>(`${this.baseUrl}/locker`, {
      headers: this.headers,
      params: httpParams,
    });
  }

  getLockerOrders(params: LockerOrderQueryParams): Observable<ApiLockerOrder[]> {
    let httpParams = new HttpParams()
      .set('chain_id', params.chain_id);
    if (params.locker_id)
      httpParams = httpParams.set('locker_id', params.locker_id);
    if (params.user_address)
      httpParams = httpParams.set('user_address', params.user_address);
    httpParams = this.appendPagination(httpParams, params);
    return this.http
      .get<PaginatedResponse<ApiLockerOrder>>(
        `${this.baseUrl}/locker_orders`,
        { headers: this.headers, params: httpParams },
      )
      .pipe(
        this.unwrapList(),
        map((list) => list.map((o) => this.normalizeLockerOrder(o))),
      );
  }

  getLockerUnlockOrders(
    params: LockerOrderQueryParams,
  ): Observable<ApiLockerUnlockOrder[]> {
    let httpParams = new HttpParams()
      .set('chain_id', params.chain_id);
    if (params.locker_id)
      httpParams = httpParams.set('locker_id', params.locker_id);
    if (params.user_address)
      httpParams = httpParams.set('user_address', params.user_address);
    httpParams = this.appendPagination(httpParams, params);
    return this.http
      .get<PaginatedResponse<ApiLockerUnlockOrder>>(
        `${this.baseUrl}/locker_unlock_orders`,
        { headers: this.headers, params: httpParams },
      )
      .pipe(
        this.unwrapList(),
        map((list) =>
          list.map((u) => ({
            ...u,
            unlock_amount: normalizeWeiString(String(u.unlock_amount), 18) ?? '0',
          })),
        ),
      );
  }

  // =========================================================================
  // Signatures
  // =========================================================================

  createBetSignature(
    body: BetSignatureRequest,
  ): Observable<BetSignatureResponse> {
    return this.http.post<BetSignatureResponse>(
      `${this.baseUrl}/signatures/bet`,
      body,
      { headers: this.headers },
    );
  }

  createTransferMessageHash(
    body: TransferMessageHashRequest,
  ): Observable<TransferMessageHashResponse> {
    return this.http.post<TransferMessageHashResponse>(
      `${this.baseUrl}/signatures/transfer-message-hash`,
      body,
      { headers: this.headers },
    );
  }

  // =========================================================================
  // TVL — no backend endpoint yet, fallback to mock data
  // =========================================================================

  getTvl(): Observable<ApiTvl> {
    return of(MOCK_TVL_DATA);
  }

  getTvlHistory(range: string): Observable<ApiTvlHistory[]> {
    const data = MOCK_TVL_HISTORY[range] ?? MOCK_TVL_HISTORY['total'];
    return of(data);
  }

  // =========================================================================
  // Price history — no backend endpoint yet, fallback to mock data
  // =========================================================================

  getPriceHistory(_marketId: string): Observable<ApiPriceHistory[]> {
    return of(MOCK_PRICE_HISTORY);
  }

  // =========================================================================
  // Token info — no backend endpoint yet, fallback to mock data
  // =========================================================================

  getTokenInfo(_tokenSymbol: string): Observable<ApiTokenInfo> {
    return of(MOCK_TOKEN_INFO);
  }

  // =========================================================================
  // Market Price History (real API)
  // =========================================================================

  getMarketPriceHistory(params: { chain_id: number; market_id: string }): Observable<{ price: number; captured_at: string; timestamp?: string }[]> {
    // ALB FastAPI uses REST path-style + paginated envelope with string prices.
    // Transform back to the array-of-number shape the rest of the app expects.
    const httpParams = new HttpParams()
      .set('chain_id', params.chain_id)
      .set('limit', 20);
    return this.http
      .get<{ data: { price: string; captured_at: string; price_source?: string }[] }>(
        `${this.baseUrl}/market/${params.market_id}/price_history`,
        { headers: this.headers, params: httpParams },
      )
      .pipe(
        map((res) => (res?.data ?? []).map((h) => ({
          price: Number(h.price),
          captured_at: h.captured_at,
        }))),
      );
  }

  // =========================================================================
  // Locker Price (real API)
  // =========================================================================

  getLockerPrice(params: { chain_id: number; locker_id: string }): Observable<{ price: number }> {
    // ALB FastAPI exposes this under /locker/{id}/price and returns price as a
    // string (Decimal). Coerce to number so consumers keep their existing contract.
    const httpParams = new HttpParams().set('chain_id', params.chain_id);
    return this.http
      .get<{ price: string }>(
        `${this.baseUrl}/locker/${params.locker_id}/price`,
        { headers: this.headers, params: httpParams },
      )
      .pipe(map((res) => ({ price: Number(res?.price ?? 0) })));
  }

  // =========================================================================
  // TVL (real API)
  // =========================================================================

  // Backend ships usd_value as a decimal string (D1 NUMERIC). Coerce at the
  // boundary once so downstream code sees an honest `number`.
  private normalizeTvlPoints(
    raw: { time: string; usd_value: string | number }[],
  ): { time: string; usd_value: number }[] {
    return raw.map((h) => ({ time: h.time, usd_value: Number(h.usd_value) }));
  }

  getTotalTvl(): Observable<{ time: string; usd_value: number }[]> {
    return this.http
      .get<{ time: string; usd_value: string | number }[]>(
        `${this.baseUrl}/total_tvl`,
        { headers: this.headers },
      )
      .pipe(map((raw) => this.normalizeTvlPoints(raw)));
  }

  getTrustLockerTvl(): Observable<{ time: string; usd_value: number }[]> {
    return this.http
      .get<{ time: string; usd_value: string | number }[]>(
        `${this.baseUrl}/trust_locker_tvl`,
        { headers: this.headers },
      )
      .pipe(map((raw) => this.normalizeTvlPoints(raw)));
  }

  getRollLockerTvl(): Observable<{ time: string; usd_value: number }[]> {
    return this.http
      .get<{ time: string; usd_value: string | number }[]>(
        `${this.baseUrl}/roll_locker_tvl`,
        { headers: this.headers },
      )
      .pipe(map((raw) => this.normalizeTvlPoints(raw)));
  }

  // =========================================================================
  // Staking Rewards
  // =========================================================================

  getStakingRewardsPending(params: { user_address: string }): Observable<StakingRewardPending[]> {
    const httpParams = new HttpParams().set('user_address', params.user_address);
    return this.http.get<StakingRewardPending[]>(`${this.baseUrl}/staking_rewards/pending`, { headers: this.headers, params: httpParams });
  }

  getStakingRewardsHistory(params: { user_address: string }): Observable<StakingRewardSnapshot[]> {
    const httpParams = new HttpParams().set('user_address', params.user_address);
    return this.http.get<PaginatedResponse<StakingRewardSnapshot>>(`${this.baseUrl}/staking_rewards`, { headers: this.headers, params: httpParams }).pipe(this.unwrapList());
  }

  // =========================================================================
  // Locker reward distribution tables (Guy backend 2026-04)
  // These call the generic /table/{name} endpoint but are allowed in production
  // because they are read-only and scoped to specific tables.
  // =========================================================================

  getLockerRecords(params: { limit?: number; user_address?: string }): Observable<LockerRecord[]> {
    const httpParams = new HttpParams()
      .set('limit', params.limit ?? 1000)
      .set('order_by', 'date')
      .set('desc', 'true');
    return this.http
      .get<TableRowsResponse>(`${this.baseUrl}/table/locker_record`, {
        headers: this.headers,
        params: httpParams,
      })
      .pipe(
        map((res) => {
          const rows = (res.rows ?? []) as unknown as LockerRecord[];
          return params.user_address
            ? rows.filter(
                (r) =>
                  r.user_address?.toLowerCase() ===
                  params.user_address!.toLowerCase(),
              )
            : rows;
        }),
      );
  }

  getLockerClaims(params: { limit?: number; locker_id?: string }): Observable<LockerClaim[]> {
    const httpParams = new HttpParams()
      .set('limit', params.limit ?? 100)
      .set('order_by', 'date')
      .set('desc', 'true');
    return this.http
      .get<TableRowsResponse>(`${this.baseUrl}/table/locker_claim`, {
        headers: this.headers,
        params: httpParams,
      })
      .pipe(
        map((res) => {
          const rows = (res.rows ?? []) as unknown as LockerClaim[];
          return params.locker_id
            ? rows.filter((r) => r.locker_id === params.locker_id)
            : rows;
        }),
      );
  }

  // =========================================================================
  // Generic Table (admin/debug only)
  // =========================================================================

  getTables(): Observable<{ tables: string[] }> {
    if (this.env.isProduction) return throwError(() => new Error('Debug endpoint disabled in production'));
    return this.http.get<{ tables: string[] }>(`${this.baseUrl}/tables`, {
      headers: this.headers,
    });
  }

  getTableSchema(
    tableName: string,
  ): Observable<{ table: string; columns: TableSchemaColumn[] }> {
    if (this.env.isProduction) return throwError(() => new Error('Debug endpoint disabled in production'));
    return this.http.get<{ table: string; columns: TableSchemaColumn[] }>(
      `${this.baseUrl}/table/${tableName}/schema`,
      { headers: this.headers },
    );
  }

  getTableCount(
    tableName: string,
  ): Observable<{ table: string; count: number }> {
    if (this.env.isProduction) return throwError(() => new Error('Debug endpoint disabled in production'));
    return this.http.get<{ table: string; count: number }>(
      `${this.baseUrl}/table/${tableName}/count`,
      { headers: this.headers },
    );
  }

  getTableRows(
    tableName: string,
    params?: TableRowsParams,
  ): Observable<TableRowsResponse> {
    // Whitelist a small set of tables that the FE legitimately reads in
    // production (Wesley 2026-05-13 confirmed backend allows the generic
    // /table/{name} endpoint — only the FE was gating). Everything else
    // remains an admin/debug-only path.
    if (this.env.isProduction && !PROD_ALLOWED_TABLES.has(tableName)) {
      return throwError(() => new Error('Debug endpoint disabled in production'));
    }
    let httpParams = new HttpParams();
    if (params?.limit !== undefined)
      httpParams = httpParams.set('limit', params.limit);
    if (params?.offset !== undefined)
      httpParams = httpParams.set('offset', params.offset);
    if (params?.order_by)
      httpParams = httpParams.set('order_by', params.order_by);
    if (params?.desc !== undefined)
      httpParams = httpParams.set('desc', params.desc);
    return this.http.get<TableRowsResponse>(
      `${this.baseUrl}/table/${tableName}`,
      { headers: this.headers, params: httpParams },
    );
  }
}
